import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { fmt, fmtDate } from "../lib/utils";
import {
  Button, Input, Select, FormField,
  Spinner, PageHeader, Card, CardContent, CardHeader, CardTitle,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Download } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "trabajos",    label: "Trabajos" },
  { id: "ingresos",    label: "Ingresos" },
  { id: "tecnicos",    label: "Técnicos" },
  { id: "repuestos",   label: "Repuestos" },
  { id: "clientes",    label: "Clientes" },
  { id: "gastos",      label: "Gastos" },
];

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
      }`}
    >
      {children}
    </button>
  );
}

function exportCsv(filename, headers, rows) {
  const lines = [headers.join(","), ...rows.map((r) => r.join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── Tab: Trabajos ────────────────────────────────────────────────────────────
function TabTrabajos() {
  const [data, setData]  = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta,  setHasta]  = useState(new Date().toISOString().slice(0,10));
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    const r = await api.get("/reportes/trabajos", { params: { desde: desde||undefined, hasta: hasta||undefined } });
    setData(r.data);
    setLoading(false);
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  function doExport() {
    exportCsv("trabajos.csv",
      ["N° OT","Vehículo","Cliente","Técnico","Estado","Inicio","Entrega"],
      data.map((r) => [r.numero, r.vehiculo_desc, r.cliente_nombre, r.tecnico_nombre??"-", r.estado, fmtDate(r.created_at), fmtDate(r.fecha_real)])
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <FormField label="Desde"><Input type="date" value={desde} onChange={(e)=>setDesde(e.target.value)} /></FormField>
        <FormField label="Hasta"><Input type="date" value={hasta} onChange={(e)=>setHasta(e.target.value)} /></FormField>
        <Button onClick={load}>Aplicar</Button>
        <Button variant="outline" onClick={doExport}><Download className="w-4 h-4" /> CSV</Button>
      </div>
      {loading ? <Spinner /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>N° OT</TableHead><TableHead>Vehículo</TableHead>
              <TableHead>Cliente</TableHead><TableHead>Técnico</TableHead>
              <TableHead>Estado</TableHead><TableHead>Inicio</TableHead><TableHead>Entregado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.numero}</TableCell>
                <TableCell className="text-sm">{r.vehiculo_desc}</TableCell>
                <TableCell className="text-sm">{r.cliente_nombre}</TableCell>
                <TableCell className="text-sm">{r.tecnico_nombre ?? "—"}</TableCell>
                <TableCell className="text-xs">{r.estado}</TableCell>
                <TableCell className="text-sm">{fmtDate(r.created_at)}</TableCell>
                <TableCell className="text-sm">{r.fecha_real ? fmtDate(r.fecha_real) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ─── Tab: Ingresos ────────────────────────────────────────────────────────────
function TabIngresos() {
  const [data, setData] = useState([]);
  useEffect(() => {
    api.get("/dashboard/ingresos-mensuales").then((r) => setData(r.data));
  }, []);
  return (
    <Card>
      <CardHeader><CardTitle>Ingresos por mes</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Bar dataKey="total" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() =>
            exportCsv("ingresos.csv", ["Mes","Total"],
              data.map((r) => [r.mes, r.total]))
          }>
            <Download className="w-4 h-4" /> Exportar CSV
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Tab: Técnicos ────────────────────────────────────────────────────────────
function TabTecnicos() {
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/reportes/tecnicos-productividad").then((r) => setData(r.data)); }, []);
  return (
    <Card>
      <CardHeader><CardTitle>Productividad de técnicos</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} layout="vertical" margin={{ left: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis type="number" tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="tecnico" tick={{ fontSize: 12 }} width={80} />
            <Tooltip />
            <Bar dataKey="completados" fill="hsl(var(--accent))" radius={[0,4,4,0]}>
              {data.map((_, i) => <Cell key={i} fill="hsl(var(--accent))" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() =>
            exportCsv("tecnicos.csv", ["Técnico","Trabajos completados"],
              data.map((r) => [r.tecnico, r.completados]))
          }>
            <Download className="w-4 h-4" /> Exportar CSV
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Tab: Repuestos ───────────────────────────────────────────────────────────
function TabRepuestos() {
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/reportes/repuestos-uso").then((r) => setData(r.data)); }, []);
  return (
    <Card>
      <CardHeader><CardTitle>Repuestos más utilizados</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} layout="vertical" margin={{ left: 120 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="nombre" tick={{ fontSize: 11 }} width={120} />
            <Tooltip />
            <Bar dataKey="total" fill="hsl(160 84% 39%)" radius={[0,4,4,0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() =>
            exportCsv("repuestos-uso.csv", ["Repuesto","Unidades usadas"],
              data.map((r) => [r.nombre, r.total]))
          }>
            <Download className="w-4 h-4" /> Exportar CSV
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Tab: Clientes ────────────────────────────────────────────────────────────
function TabClientes() {
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/reportes/clientes-ranking").then((r) => setData(r.data)); }, []);
  return (
    <Card>
      <CardHeader><CardTitle>Clientes con más trabajos</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead><TableHead>Cliente</TableHead><TableHead className="text-right">Trabajos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="text-muted-foreground text-xs w-8">{i+1}</TableCell>
                <TableCell>{r.cliente}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{r.trabajos}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() =>
            exportCsv("clientes-ranking.csv", ["Cliente","Trabajos"],
              data.map((r) => [r.cliente, r.trabajos]))
          }>
            <Download className="w-4 h-4" /> Exportar CSV
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Tab: Gastos ──────────────────────────────────────────────────────────────
function TabGastos() {
  const [mes,  setMes]  = useState(new Date().getMonth() + 1);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  async function load() {
    const r = await api.get("/gastos", { params: { mes, anio } });
    setData(r.data);
    setTotal(r.data.reduce((s, g) => s + (g.monto ?? 0), 0));
  }

  useEffect(() => { load(); }, [mes, anio]); // eslint-disable-line

  // Agrupar por categoría para gráfico
  const bycat = data.reduce((acc, g) => {
    acc[g.categoria] = (acc[g.categoria] ?? 0) + g.monto;
    return acc;
  }, {});
  const chartData = Object.entries(bycat).map(([k, v]) => ({ categoria: k, monto: v }));

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end">
        <FormField label="Mes">
          <Select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {[...Array(12)].map((_, i) => (
              <option key={i+1} value={i+1}>{new Date(2024,i).toLocaleString("es", { month: "long" })}</option>
            ))}
          </Select>
        </FormField>
        <FormField label="Año">
          <Input type="number" min="2020" max="2099" value={anio} onChange={(e) => setAnio(Number(e.target.value))} className="w-24" />
        </FormField>
      </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Por categoría</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="categoria" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="monto" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Detalle · <span className="text-primary">{fmt(total)}</span></CardTitle>
          </CardHeader>
          <CardContent className="overflow-y-auto max-h-[220px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell className="text-sm">{g.descripcion}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{g.categoria}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmt(g.monto)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Button variant="outline" size="sm" onClick={() =>
        exportCsv("gastos.csv", ["Descripción","Categoría","Monto","Fecha"],
          data.map((r) => [r.descripcion, r.categoria, r.monto, fmtDate(r.fecha)]))
      }>
        <Download className="w-4 h-4" /> Exportar CSV
      </Button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Reportes() {
  const [tab, setTab] = useState("trabajos");
  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Reportes" sub="Estadísticas y exportaciones" />
      <div className="flex flex-wrap gap-1 p-1 bg-secondary rounded-lg mb-5 overflow-x-auto">
        {TABS.map((t) => (
          <TabButton key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</TabButton>
        ))}
      </div>
      {tab === "trabajos"  && <TabTrabajos />}
      {tab === "ingresos"  && <TabIngresos />}
      {tab === "tecnicos"  && <TabTecnicos />}
      {tab === "repuestos" && <TabRepuestos />}
      {tab === "clientes"  && <TabClientes />}
      {tab === "gastos"    && <TabGastos />}
    </div>
  );
}
